import { FilterFilled, FilterOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Alert, Select, Switch } from "antd";
import { cascadeApi } from "api/TenantSlug/Cascade/api";
import {
    useBatchUpdateCascadeMutation,
    useBatchDeleteCascadeMutation,
    useExportCascadesMutation,
} from "api/TenantSlug/Cascade/hooks.ts";
import classNames from "classnames";
import { AddCascadeDrawer } from "components/Drawers/AddCascadeDrawer/AddCascadeDrawer.tsx";
import { CascadeDetailDrawer } from "components/Drawers/CascadeDetailDrawer/CascadeDetailDrawer.tsx";
import { CascadeFilterDrawer } from "components/Drawers/CascadeFilterDrawer/CascadeFilterDrawer.tsx";
import { countFilterRules } from "components/UI/BaseFilter/utils/filterTreeMapper.ts";
// import { DrawerLink } from "components/UI/DrawerLink";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { IntegrationSelectSearchable } from "components/UI/Selects/IntegrationSelectSearchable/IntegrationSelectSearchable.tsx";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom, ExportRange } from "components/UI/Table/atoms/exportAtom";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { createNumberFilterType, FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableUrlParameters } from "components/UI/Table/hooks/useTableUrlParameters.ts";
import { QuickFilterOption, TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DraggableTableRow } from "components/UI/Table/UI/DragAndDrop/DraggableTableRow";
import { TableRow } from "components/UI/Table/UI/Editable/TableRow";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell";
import { normalizeValuelessFilterOperators } from "components/UI/Table/utils/normalizeValuelessFilterOperators.ts";
import { useDrawer } from "hooks/useDrawer";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { CascadeAction, CascadeDistributionType, CascadeStatus, PermissionEnum, SearchFilterEnum } from "types/enums";
import { DrawerId, FormId, TableId } from "types/models";
import { isUUID } from "utils/isUUID";
import { updateStateOnDragEnd } from "utils/updateStateOnDragEnd";

import { ChannelItemFormData, pingtreeFormSchema, PingtreeFormData } from "./formSchema.ts";

import UpdateCascadeRequest = App.Http.Requests.Cascade.UpdateCascadeRequest;
import CascadeData = App.Data.CascadeData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

const nullableNumberFilterType = createNumberFilterType({ includeNullOperators: true });

interface BatchUpdateCascadeItem extends UpdateCascadeRequest {
    id: string;
}

interface PingtreeFormContentProps {
    offerId: string;
    items: string[];
    setItems: React.Dispatch<React.SetStateAction<string[]>>;
    deletedIds: (number | string)[];
    setDeletedIds: React.Dispatch<React.SetStateAction<(number | string)[]>>;
    allCascadesData: CascadeData[] | undefined;
    cascadesData: CascadeData[] | undefined;
    isLoading: boolean;
    onCascadeCreated: () => void;
    structureOptions: QuickFilterOption[];
    integrationOptions: QuickFilterOption[];
    onExport: () => Promise<string | undefined>;
    onRefresh: () => void;
    hasActiveTableFilters: boolean;
}

const PingtreeFormContent: FC<PingtreeFormContentProps> = ({
    offerId,
    items,
    setItems,
    deletedIds,
    setDeletedIds,
    allCascadesData,
    cascadesData,
    isLoading,
    onCascadeCreated,
    structureOptions,
    integrationOptions,
    onExport,
    onRefresh,
    hasActiveTableFilters,
}) => {
    const drawerUtils = useDrawer();
    const { t: tP } = usePaldockTranslation("pages.offer.edit.pingtree.columns");
    const { t: tM } = usePaldockTranslation("pages.offer.edit.pingtree.messages");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tG } = usePaldockTranslation("general");

    const { getValues, reset, control } = useGeneralForm<PingtreeFormData>();
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const isDraggingRef = useRef(false);
    const { getRowHandlers, getRowClassName } = useRowHandlers<CascadeData>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.CascadeDetail,
                drawerParam: `${DrawerId.CascadeDetail}-id`,
            },
        ],
        excludeColumns: [
            "title",
            "visible_title",
            "id",
            "integration_id",
            "distribution",
            "action",
            "bid_min_accepted",
            "commission_action",
            "view",
            "filter_action",
            "cascadeFilter",
            "status",
        ],
    });

    // Initialize form values when cascades data changes
    useEffect(() => {
        if (allCascadesData && allCascadesData.length > 0) {
            const currentChannels = getValues("channels") || {};
            const cascadesIds = allCascadesData.map((cascade) => cascade.id);
            const cascadesIdSet = new Set(cascadesIds);
            const channelsData: Record<string, ChannelItemFormData> = {};

            allCascadesData.forEach((c: CascadeData) => {
                channelsData[c.id] =
                    currentChannels[c.id] ??
                    ({
                        title: c.title,
                        visible_title: c.visible_title,
                        integration: c.integration_id ?? "",
                        distribution: c.distribution,
                        action: c.action,
                        bid_min_accepted: c.bid_min_accepted,
                        active: c.status === CascadeStatus.Enabled,
                    } satisfies ChannelItemFormData);
            });

            setItems((currentItems) => {
                const existing = new Set(currentItems);
                const kept = currentItems.filter((id) => cascadesIdSet.has(id));
                const newIds = cascadesIds.filter((id) => !existing.has(id));
                return [...kept, ...newIds];
            });
            reset({ channels: channelsData });
        } else if (allCascadesData) {
            // If cascadesData is empty array, reset everything
            setItems([]);
            reset({ channels: {} });
        }
    }, [allCascadesData, getValues, setItems, reset]);

    const distributionOptions = useMemo(() => {
        return Object.values(CascadeDistributionType).map((value) => ({
            label: tG(`cascade_distribution_type.${value}`),
            value,
        }));
    }, [tG]);

    const actionOptions = useMemo(() => {
        return Object.values(CascadeAction).map((value) => ({
            label: tG(`cascade_action.${value}`),
            value,
        }));
    }, [tG]);

    const handleDeleteCascade = (id: string) => {
        if (!isUUID(id)) {
            setItems((prevState) => prevState.filter((item) => item !== id));
        } else {
            setDeletedIds((prevState) => (prevState.includes(id) ? prevState : [...prevState, id]));
        }
    };

    const dataSource = useMemo(() => {
        if (!cascadesData) return [];
        const cascadesById = new Map(cascadesData.map((cascade) => [cascade.id, cascade]));
        return items.map((id) => cascadesById.get(id)).filter((cascade): cascade is CascadeData => Boolean(cascade));
    }, [items, cascadesData]);

    const columnConfig: TableColumnType<CascadeData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tP("system_name")} />,
            dataIndex: "title",
            key: "title",
            render: (_, record) => (
                <InteractiveCell.Input formControl={{ control, name: `channels.${record.id}.title` }} overlay={true} />
            ),
            filterable: true,
            filterType: FILTER_TYPES.string,
            width: 160,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("visible_name")} />,
            dataIndex: "visible_title",
            key: "visible_title",
            render: (_, record) => (
                <InteractiveCell.Input
                    formControl={{ control, name: `channels.${record.id}.visible_title` }}
                    overlay={true}
                />
            ),
            filterable: true,
            filterType: FILTER_TYPES.string,
            width: 160,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("structure")} />,
            dataIndex: "integration.form_structure.name",
            key: "structure",
            render: (_, record) => record.integration?.form_structure?.name ?? "-",
            filterable: true,
            filterName: "form_structure_id",
            filterType: {
                type: "select",
                operators: [SearchFilterEnum.Equal],
            },
            filterOptions: structureOptions,
            width: 180,
            visible: false,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("id")} />,
            dataIndex: "id",
            key: "id",
            render: (id) => <ShortenedIdWithCopy id={id} />,
            width: 100,
            visible: false,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("integration")} />,
            dataIndex: "integration.name",
            key: "integration_id",
            filterName: "integration_id",
            render: (_, record) => (
                <FormItem control={control} name={`channels.${record.id}.integration`} noStyle>
                    <IntegrationSelectSearchable
                        variant="borderless"
                        className="pingtree-editable-select w-full"
                        popupMatchSelectWidth={false}
                        onClick={(e) => e.stopPropagation()}
                    />
                </FormItem>
            ),
            filterable: true,
            filterType: {
                type: "select",
                operators: [SearchFilterEnum.Equal],
            },
            filterOptions: integrationOptions,
            width: 180,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("distribution")} />,
            dataIndex: "distribution",
            key: "distribution",
            render: (_, record) => (
                <FormItem control={control} name={`channels.${record.id}.distribution`} noStyle>
                    <Select
                        options={distributionOptions}
                        variant="borderless"
                        className="pingtree-editable-select w-full"
                        popupMatchSelectWidth={false}
                        onClick={(e) => e.stopPropagation()}
                    />
                </FormItem>
            ),
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: distributionOptions,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("redirect")} />,
            dataIndex: "action",
            key: "action",
            render: (_, record) => (
                <FormItem control={control} name={`channels.${record.id}.action`} noStyle>
                    <Select
                        options={actionOptions}
                        variant="borderless"
                        className="pingtree-editable-select w-full"
                        popupMatchSelectWidth={false}
                        onClick={(e) => e.stopPropagation()}
                    />
                </FormItem>
            ),
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: actionOptions,
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("min_price")} />,
            dataIndex: "bid_min_accepted",
            key: "bid_min_accepted",
            render: (_, record) => (
                <InteractiveCell.NumberInput
                    formControl={{ control, name: `channels.${record.id}.bid_min_accepted` }}
                    overlay={true}
                    clearable
                />
            ),
            filterable: true,
            filterType: nullableNumberFilterType,
            width: 110,
            ellipsis: true,
        },
        /*
        {
            title: <ColumnHeaderTitle columnTitle={tP("provision")} />,
            dataIndex: "commission_action",
            key: "view",
            render: (_, record) => (
                <DrawerLink
                    drawerId={DrawerId.CascadeCommissions}
                    search={{ [`${DrawerId.CascadeCommissions}-id`]: record.id }}
                    action="show"
                >
                    <Button type="link" className="!underline hover:!no-underline" size="small">
                        {tC("view")}
                    </Button>
                </DrawerLink>
            ),
            width: 90,
        },
        */
        {
            title: <ColumnHeaderTitle columnTitle={tP("filter")} />,
            dataIndex: "filter_action",
            key: "cascadeFilter",
            render: (_, record) => (
                <span
                    className="inline-flex cursor-pointer"
                    data-row-click-ignore="true"
                    onClick={(event) => {
                        event.stopPropagation();
                        drawerUtils.show(DrawerId.CascadeFilter, {
                            [`${DrawerId.CascadeFilter}-id`]: record.id,
                        });
                    }}
                >
                    {countFilterRules(record.filter) > 0 ? (
                        <FilterFilled style={{ color: "#008080" }} title={String(countFilterRules(record.filter))} />
                    ) : (
                        <FilterOutlined />
                    )}
                </span>
            ),
            width: 70,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tP("active")} />,
            dataIndex: "status",
            key: "status",
            render: (_, record) => (
                <span data-row-click-ignore="true" className="inline-flex">
                    <FormItem control={control} name={`channels.${record.id}.active`} noStyle>
                        <Switch />
                    </FormItem>
                </span>
            ),
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: [
                { label: tC("active"), value: CascadeStatus.Enabled },
                { label: tC("paused"), value: CascadeStatus.Disabled },
            ],
            width: 90,
            ellipsis: true,
        },
    ];

    return (
        <>
            <AddCascadeDrawer offerId={offerId} sort={allCascadesData?.length ?? 0} onSuccess={onCascadeCreated} />
            <CascadeDetailDrawer cascades={allCascadesData ?? []} onDelete={handleDeleteCascade} />
            <CascadeFilterDrawer onFilterSaved={onRefresh} />
            <DndContext
                sensors={hasActiveTableFilters ? [] : sensors}
                collisionDetection={closestCenter}
                onDragStart={() => {
                    isDraggingRef.current = true;
                }}
                onDragCancel={() => {
                    isDraggingRef.current = false;
                }}
                onDragEnd={(event) => {
                    if (hasActiveTableFilters) {
                        isDraggingRef.current = false;
                        return;
                    }

                    updateStateOnDragEnd(event, setItems);
                    window.setTimeout(() => {
                        isDraggingRef.current = false;
                    }, 0);
                }}
            >
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {hasActiveTableFilters && (
                        <Alert className="mb-2" type="info" showIcon message={tM("reordering_disabled_by_filters")} />
                    )}
                    <Table<Record<string, never>, CascadeData>
                        tableId={TableId.PingtreeCascadesTable}
                        data={{}}
                        dataSource={dataSource}
                        columnConfig={columnConfig}
                        loading={isLoading}
                        rowKey={(record) => record.id}
                        pagination={false}
                        enableDragAndDrop={false}
                        enableFilters={true}
                        quickFilterDataIndex="form_structure_id"
                        quickFilterOptions={structureOptions}
                        addEntityData={{
                            drawerId: DrawerId.AddCascade,
                            permission: PermissionEnum.CascadeCreate,
                        }}
                        onExport={onExport}
                        exportRangeOptions={[
                            { value: "all", label: "range.all_available" } as { value: ExportRange; label: string },
                        ]}
                        onRefresh={onRefresh}
                        showRefetchButton={true}
                        onRow={(record, rowIndex) => {
                            const rowHandlers = getRowHandlers(record, rowIndex);

                            return {
                                ...rowHandlers,
                                onClick: (event) => {
                                    const clickTarget = event.target as HTMLElement;
                                    const isIgnoredRowAction = clickTarget.closest('[data-row-click-ignore="true"]');

                                    if (isDraggingRef.current || isIgnoredRowAction) {
                                        return;
                                    }

                                    rowHandlers.onClick(event);
                                },
                            };
                        }}
                        rowClassName={(record, rowIndex) =>
                            classNames(getRowClassName(rowIndex ?? 0), deletedIds.includes(record.id) && "opacity-50")
                        }
                        components={{ body: { row: hasActiveTableFilters ? TableRow : DraggableTableRow } }}
                        autoScrollWidth={true}
                    />
                </SortableContext>
            </DndContext>
        </>
    );
};

interface PingtreeFormProps {
    offerId: string;
}

export const PingtreeForm: FC<PingtreeFormProps> = ({ offerId }) => {
    const [items, setItems] = useState<Array<string>>([]);
    const [deletedIds, setDeletedIds] = useState<Array<number | string>>([]);
    const cascadeTableState = useRecoilValue(tableAtom<CascadeData>()(TableId.PingtreeCascadesTable));
    const cascadeTableUrlParameters = useTableUrlParameters<CascadeData>(TableId.PingtreeCascadesTable);
    const exportState = useRecoilValue(exportAtom(TableId.PingtreeCascadesTable));
    const { t: tF } = usePaldockTranslation("forms.pingtree-form");

    const cascadeFormId = FormId.CascadeForm;
    const exportCascadesMutation = useExportCascadesMutation();

    const fetchCascades = useCallback((payload: App.Http.Requests.Cascade.GetCascadesRequest) => {
        return cascadeApi.index(payload);
    }, []);

    // Fetch all cascades using the hook
    const allCascadesQuery = useFetchAllPaginatedData(
        fetchCascades,
        {
            filter: {
                filter: {
                    offer_id: offerId,
                },
                include: "integration.formStructure",
                sort: "sort",
            },
        },
        "cascades"
    );

    const urlTableFilter = cascadeTableUrlParameters.getUrlParameters()?.filter;
    const activeTableFilters = normalizeValuelessFilterOperators(
        urlTableFilter?.filter ?? cascadeTableState.filter?.filter ?? {}
    );
    const hasActiveTableFilters = Object.keys(activeTableFilters).length > 0;
    const cascadesRequestFilter: App.Http.Requests.FilterRequest = {
        ...(urlTableFilter ?? cascadeTableState.filter),
        filter: {
            ...activeTableFilters,
            offer_id: offerId,
        },
        include: "integration.formStructure",
        sort: "sort",
    };
    const filteredCascadesQuery = useFetchAllPaginatedData(
        fetchCascades,
        { filter: cascadesRequestFilter },
        "cascades",
        { enabled: hasActiveTableFilters }
    );

    const handleExport = async (): Promise<string | undefined> => {
        try {
            const response = await exportCascadesMutation.mutateAsync({
                filter: {
                    filter: cascadesRequestFilter.filter,
                    include: cascadesRequestFilter.include,
                    sort: cascadesRequestFilter.sort,
                },
                extension: exportState.extension,
                headers: exportState.headers,
                export_all: exportState.range === "all",
                delimiter: exportState.delimiter,
            } as BaseExportRequest);
            return response?.url;
        } catch {
            return undefined;
        }
    };

    const visibleCascadesData = hasActiveTableFilters ? filteredCascadesQuery.data : allCascadesQuery.data;
    const structureOptions = useMemo<QuickFilterOption[]>(() => {
        const structures = new Map<string, string>();

        allCascadesQuery.data.forEach((cascade) => {
            const structure = cascade.integration?.form_structure;
            if (structure?.id) {
                structures.set(structure.id, structure.name);
            }
        });

        return Array.from(structures, ([value, label]) => ({ value, label })).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [allCascadesQuery.data]);

    const integrationOptions = useMemo<QuickFilterOption[]>(() => {
        const integrations = new Map<string, string>();

        allCascadesQuery.data.forEach((cascade) => {
            const integration = cascade.integration;
            if (integration?.id) {
                integrations.set(integration.id, integration.name);
            }
        });

        return Array.from(integrations, ([value, label]) => ({ value, label })).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [allCascadesQuery.data]);

    const refetchCascades = async () => {
        await allCascadesQuery.refetch();
        if (hasActiveTableFilters) {
            await filteredCascadesQuery.refetch();
        }
    };

    const batchUpdateCascadeMutation = useBatchUpdateCascadeMutation({
        onError: () => toast.error("Failed to update cascade"),
    });

    const batchDeleteCascadeMutation = useBatchDeleteCascadeMutation({
        onError: () => toast.error("Failed to delete cascades"),
    });

    const handleSubmit = async (values: PingtreeFormData) => {
        const cascadesToUpdate: BatchUpdateCascadeItem[] = [];
        const channels = values.channels || {};

        try {
            // Batch delete all cascades that have UUIDs (existing cascades)
            const cascadeIdsToDelete = deletedIds.filter((id) => isUUID(id.toString())).map((id) => id.toString());
            if (cascadeIdsToDelete.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchDeleteCascadeMutation.mutate(
                        { ids: cascadeIdsToDelete },
                        {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        }
                    );
                });
            }

            Object.keys(channels).forEach((key) => {
                // Work only with undeleted, persisted cascades (new ones are created immediately via the drawer)
                if (!deletedIds.includes(key) && isUUID(key)) {
                    const channelData = channels[key];
                    const updateRequest: BatchUpdateCascadeItem = {
                        id: key,
                        sort: items.findIndex((item) => item === key),
                        integration_id: channelData.integration,
                        title: channelData.title,
                        visible_title: channelData.visible_title,
                        distribution: channelData.distribution as CascadeDistributionType,
                        action: channelData.action as CascadeAction,
                        bid_min_accepted: channelData.bid_min_accepted,
                        status: channelData.active ? CascadeStatus.Enabled : CascadeStatus.Disabled,
                    };
                    cascadesToUpdate.push(updateRequest);
                }
            });

            // Batch update existing cascades
            if (cascadesToUpdate.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchUpdateCascadeMutation.mutate(
                        { cascades: cascadesToUpdate },
                        {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        }
                    );
                });
            }

            toast.success(tF("messages.update_success"));

            // Reset state after successful save
            setDeletedIds([]);

            // Refetch cascades data to show actual server state
            await refetchCascades();
        } catch (error) {
            console.error("Error saving cascades:", error);
        }
    };

    const defaultValues: PingtreeFormData = {
        channels: {},
    };

    return (
        <GeneralFormProvider<PingtreeFormData>
            schema={pingtreeFormSchema}
            defaultValues={defaultValues}
            id={cascadeFormId}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <PingtreeFormContent
                offerId={offerId}
                items={items}
                setItems={setItems}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
                allCascadesData={allCascadesQuery.data}
                cascadesData={visibleCascadesData}
                isLoading={
                    allCascadesQuery.isLoading ||
                    allCascadesQuery.isRefetching ||
                    (hasActiveTableFilters && (filteredCascadesQuery.isLoading || filteredCascadesQuery.isRefetching))
                }
                onCascadeCreated={refetchCascades}
                structureOptions={structureOptions}
                integrationOptions={integrationOptions}
                onExport={handleExport}
                onRefresh={refetchCascades}
                hasActiveTableFilters={hasActiveTableFilters}
            />
        </GeneralFormProvider>
    );
};
